// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

// --- Decision Card Data ---

export const METHOD_CARDS = {
  cli: {
    title: '🚀 Quick Start (CLI)',
    description: 'One-time resource discovery via Builder Toolbox CLI',
    recommended: true,
    characteristics: [
      '~5 minutes to complete',
      'Just 2 steps to onboard',
      'No admin access required',
      'Re-run anytime to refresh resources',
    ],
  },
  persistent: {
    title: '🔄 Persistent (CDK / CloudFormation)',
    description: 'Continuous resource updates via infrastructure deployment',
    badge: null,
    recommended: false,
    characteristics: [
      'Resources continuously updated',
      'Requires admin access',
      'Deploy IAM roles and Config resources',
      'Coming soon: Drift detection',
    ],
  },
};

export const PERSISTENT_METHOD_CARDS = {
  cloudformation: {
    title: 'CloudFormation Templates',
    description:
      'Deploy the template directly to your AWS account. Requires admin permission. Not recommended for production accounts.',
  },
  cdk: {
    title: 'CDK Constructs',
    description:
      'Zero-configuration deployment using CDK constructs with automatic updates via package versioning. (Code change required)',
  },
};

// --- Code Snippets ---

export const cliToolInstallCode = `toolbox registry add s3://buildertoolbox-registry-resourcelens-885523369945-us-west-2/tools.json && toolbox install resourcelens`;

export const cliAdaProfileCode = `ada prof add --account <ACCOUNT_ID> --profile <ACCOUNT_NAME> --provider conduit --role IibsAdminAccess-DO-NOT-DELETE`;

export const cliOnboardCode = `resourcelens onboard --account <ACCOUNT_ID> --name "My Account"`;

export const cliOnboardProfileCode = `resourcelens onboard --profile <PROFILE_NAME>`;

export const cliOnboardCustomNameCode = `resourcelens onboard --account <ACCOUNT_ID> --name "My Account"`;

export const cdkInstallationCode = `# Step 1: Add Brazil dependency to your Config file
dependencies = {
    1.0 = {
        ResourceLensCDKConstructs = 1.0;
    };
};

# Step 2: Add npm package to your package.json
"dependencies": {
    "@amzn/resource-lens-constructs": "1.0.0"
}`;

export const cdkSingleRegionCode = `import { ResourceLensGlobalOnboarding, ResourceLensRegionalOnboarding } from '@amzn/resource-lens-constructs';
import { Stack, StackProps } from '@amzn/secure-cdk-sdo';
import { Construct } from 'constructs';

export class MyResourceLensStack extends Stack {
  constructor(scope: Construct, id: string, props?: StackProps) {
    super(scope, id, props);

    // Global resources (deploy once per account)
    new ResourceLensGlobalOnboarding(this, 'ResourceLensGlobal');

    // Regional resources (current region only)
    new ResourceLensRegionalOnboarding(this, 'ResourceLensRegional', {
      region: Stack.of(this).region,
    });
  }
}`;

export const cdkMultiRegionCode = `// Deploy this stack in us-east-1
export class ResourceLensUSEast1Stack extends Stack {
  constructor(scope: Construct, id: string, props?: StackProps) {
    super(scope, id, props);

    // Global resources (deploy only once per account)
    new ResourceLensGlobalOnboarding(this, 'ResourceLensGlobal');

    // Regional resources for us-east-1
    new ResourceLensRegionalOnboarding(this, 'ResourceLensRegional', {
      region: 'us-east-1',
    });
  }
}

// Deploy this stack in us-west-2
export class ResourceLensUSWest2Stack extends Stack {
  constructor(scope: Construct, id: string, props?: StackProps) {
    super(scope, id, props);

    // No global resources needed (already created in us-east-1 stack)
    // Regional resources for us-west-2
    new ResourceLensRegionalOnboarding(this, 'ResourceLensRegional', {
      region: 'us-west-2',
    });
  }
}`;

export const cdkDeploymentCode = `# Deploy your CDK stack using your pipeline`;

// --- Prerequisite Lists ---

export const CLI_PREREQUISITES = [
  {
    label: 'Builder Toolbox',
    href: 'https://docs.hub.amazon.dev/builder-toolbox/user-guide/getting-started/',
  },
  {
    label: 'ADA',
    href: 'https://w.amazon.com/bin/view/DevAccount/Docs',
    note: 'via Builder Toolbox (toolbox install ada)',
  },
  {
    label: 'Node 16+',
    note: 'check with node -v',
  },
];

export const PERSISTENT_PREREQUISITES = [
  'Administrative access to target AWS accounts',
  'Permissions to create IAM roles and policies',
  'Permissions to configure AWS Config (if not already enabled)',
];
